import { useEffect, useRef, useState } from "react"

import { useClerk } from "@clerk/nextjs"
import type {
  OrganizationMembershipResource,
  OrganizationResource,
} from "@clerk/nextjs/types"

interface Props {
  organization: OrganizationResource | null | undefined
  membership:
    | Pick<OrganizationMembershipResource, "organization" | "permissions">
    | null
    | undefined
  userId: string | undefined
  routeSlug: string | null | undefined
  enabled: boolean
}

export function useOrganizationSecurityAccess({
  organization,
  membership,
  userId,
  routeSlug,
  enabled,
}: Props) {
  const clerk = useClerk()
  const clerkRef = useRef(clerk)
  clerkRef.current = clerk
  const scoped = !!(
    enabled &&
    organization &&
    userId &&
    organization.slug === routeSlug &&
    membership?.organization.id === organization.id
  )
  const canConfigure =
    scoped &&
    !!organization?.selfServeSSOEnabled &&
    !!membership?.permissions.includes("org:sys_entconns:manage")
  const needsConnections =
    scoped &&
    !canConfigure &&
    !!membership?.permissions.includes("org:sys_entconns_sso_bypass:manage")
  const [attempt, setAttempt] = useState(0)
  const key = `${organization?.id}:${userId}:${routeSlug}:${attempt}`
  const [lookup, setLookup] = useState<{
    key: string
    count: number
    error: boolean
  } | null>(null)

  useEffect(() => {
    if (!needsConnections || !organization || !userId) {
      return
    }
    let current = true
    setLookup(null)
    void organization
      .getEnterpriseConnections()
      .then((connections) => {
        if (
          current &&
          clerkRef.current.organization?.id === organization.id &&
          clerkRef.current.user?.id === userId
        ) {
          setLookup({ key, count: connections.length, error: false })
        }
      })
      .catch(() => {
        if (
          current &&
          clerkRef.current.organization?.id === organization.id &&
          clerkRef.current.user?.id === userId
        ) {
          setLookup({ key, count: 0, error: true })
        }
      })
    return () => {
      current = false
    }
  }, [needsConnections, organization, userId, key])

  const result = lookup?.key === key ? lookup : null
  return {
    allowed:
      canConfigure ||
      (needsConnections && !!result && !result.error && result.count > 0),
    loading: needsConnections && !result,
    error: needsConnections && !!result?.error,
    retry: () => setAttempt((value) => value + 1),
  }
}
